import React, { useEffect, useState } from 'react';
import { CFormLabel } from '@coreui/react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { colors, formatDateToBackend } from '../components/Helper'
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import PageHeader from '../components/PageHeader';
import DateRangeSelection from '../components/DateRangeSelection';
import ViewSingleReceiptReport from '../components/ViewSingleReceiptReport';
import { Avatar } from 'primereact/avatar';
import ViewSingleUserReport from '../components/ViewSingleUserReport';

function UserReport() {
    const { userReport, userReportCharts } = useReport();
    const { currency } = useCompanySetting()
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [userId, setUserId] = useState(null);
    const [reportList, setReportList] = useState([])
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [receiptChartData, setReceiptChartData] = useState([])
    const [userRevenue, setUserRevenue] = useState({
        x: [],
        y: []
    })

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null

    const fetchUserReport = async () => {
        try {
            setIsLoading(true);
            const reports = await userReport(currentPage, rowsPerPage, sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data)
                setTotalRecords(reports.totalCount)
            } else {
                setReportList([])
                setTotalRecords(0)
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching user reports', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchUserReport();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId, startDate, endDate]);

    const fetchUserReportCharts = async () => {
        try {
            setIsLoading(true);

            const reports = await userReportCharts(startDate, endDate);
            if (reports.data.length > 0) {
                const userReceiptData = reports.data.map((item, index) => ({
                    id: item.id,
                    value: parseFloat(item.receiptCount),
                    label: item.userName,
                    color: colors[index % colors.length]
                }));

                setReceiptChartData(userReceiptData);

                const users = reports.data.map(item => item.userName);
                const revenue = reports.data.map(item => parseFloat(item.revenue));

                setUserRevenue({
                    x: users,
                    y: revenue
                })
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching user report charts', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchUserReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchUserReport()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'User Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-end'>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    {
                        userId && (
                            <ViewSingleUserReport
                                userId={userId}
                                startDate={startDate}
                                endDate={endDate}
                                setUserId={setUserId}
                            />
                        )
                    }
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {
                            userRevenue.x.length > 0 && userRevenue.y.length > 0 && (
                                <div className='w-100 text-center me-5'>
                                    <CFormLabel>User revenue</CFormLabel>
                                    <BarChart
                                        x={userRevenue.x}
                                        y={userRevenue.y}
                                        yLabel={'Revenue'}
                                    />
                                </div>
                            )
                        }
                        {
                            receiptChartData && receiptChartData.length > 0 && (
                                <div className='w-100 text-center self-center'>
                                    <CFormLabel>#User receipts</CFormLabel>
                                    <PieChart
                                        data={receiptChartData}
                                    />
                                </div>
                            )
                        }
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={reportList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No user reports found."
                        >
                            <Column
                                field="image"
                                className='receipt_report_table'
                                body={(rowData) => (
                                    <div>
                                        {
                                            rowData.image ?
                                                <Avatar image={rowData.image} shape="circle" />
                                                :
                                                <Avatar icon="pi pi-user" shape="circle" />
                                        }
                                    </div>
                                )}
                            />
                            <Column
                                field="userName"
                                header="User"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="from"
                                header="From"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="to"
                                header="To"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="firstActionAt"
                                header="First Action"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="lastActionAt"
                                header="Last Action"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="customerCount"
                                header="#Customers"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="receiptCount"
                                header="#Receipts"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="revenueCreated"
                                header="Revenue created"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.revenueCreated ? `${currency}${rowData.revenueCreated}` : `${currency}0.00`}
                                    </div>
                                )}
                            />
                            <Column
                                field="revenueReceived"
                                header="Revenue received"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                          {rowData.revenueReceived ? `${currency}${rowData.revenueReceived}` : `${currency}0.00`}
                                    </div>
                                )}
                            />
                            <Column
                                header="Action"
                                align="left"
                                className='receipt_report_table'
                                body={(rowData) => (
                                    <div className='view_button'>
                                        <p onClick={() => setUserId(rowData.id)}>More info</p>
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    );
}

export default UserReport;
